/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { css } from '@emotion/react';
import {
  ActionList,
  ActionListProps,
  color,
  Icon,
  Popover,
  spacing,
  Tooltip,
  Typography
} from '@uniquegood/realworld-studio-design';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MarginLeft4, MarginRight8 } from '@/styles';
import { useToggle, useRequest, usePromotionModal } from '@/hooks';
import { IActionTypeDefinition } from '@/components/ActionHandler/types';
import { actionTypeGroups, ACTION_TYPE_METADATA } from '@/components/ActionHandler/utils';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PromotionTriggerLocation } from '@/track';

interface IProps {
  actionDefinition?: IActionTypeDefinition;
  setActionType(actionType: ActionHandler['actionType']): void;
}

const ActionTypeSelectorWidthOverrideStyle = css`
  width: 305px;
  border-radius: 0;

  .Actions {
    border-radius: 0;
  }
`;

const PopoverScroll = css`
  overflow: hidden;
  .Content {
    overflow-y: auto;
  }
`;

// 섹션 헤더 스타일 (자세히 보기 버튼 포함)
const SectionHeaderStyle = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 305px;
  padding: 12px 16px;
  background-color: ${color.surface_default_default};
  box-shadow: inset 0px -1px 0px ${color.border_default_subdued};
  border-top: 1px solid ${color.border_default_subdued};
`;

const SectionTitleStyle = css`
  font-size: 12px;
  line-height: 16px;
  color: ${color.text_default};
`;

const DetailLinkStyle = css`
  font-size: 12px;
  line-height: 16px;
  color: ${color.palette_primary_purple_100};
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;

  &:hover {
    text-decoration: underline;
  }
`;

// Locked 오버레이 관련 스타일
const LockedSectionWrapper = css`
  position: relative;
`;

const LockedScrimStyle = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 305px;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const UpgradeButtonStyle = css`
  background: linear-gradient(90deg, #7800ff 0%, #829bff 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  padding: 6px 12px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

const DisabledActionListStyle = css`
  width: 305px;
  border-radius: 0;

  .Actions {
    border-radius: 0;
    width: 305px;
  }

  .ListItemWrap {
    color: ${color.text_disabled};
    cursor: default;

    &:hover {
      background-color: transparent;
    }
  }
`;

// TODO: 디자인 시스템으로 변경 필요
const PopoverActionStyle = css`
  .Actions {
    width: 305px;
  }
`;

function PopoverAction({ children }: { children: React.ReactNode }) {
  return <div css={PopoverActionStyle}>{children}</div>;
}

// 섹션 헤더 컴포넌트
interface SectionHeaderProps {
  title: string;
  showDetailLink?: boolean;
  onDetailClick?: () => void;
}

function SectionHeader({ title, showDetailLink, onDetailClick }: SectionHeaderProps) {
  return (
    <div css={SectionHeaderStyle}>
      <span css={SectionTitleStyle}>{title}</span>
      {showDetailLink && (
        <button type="button" css={DetailLinkStyle} onClick={onDetailClick}>
          자세히 보기
        </button>
      )}
    </div>
  );
}

// To-Do: expand_solid 아이콘 추가해야 함.
const ActionTypeSelector = ({ actionDefinition, setActionType }: IProps) => {
  const { value: popoverActive, toggle: popoverToggle } = useToggle(false);
  const { plan } = usePlanLimits();
  const { openPromotionModal } = usePromotionModal();
  const { appId } = useParams<AppParam>();

  const { data: { data: actionTypesData } = {} } = useRequest<
    CommonResponseModel<AvailableActionTypesResponseModel>
  >(`/apps/${appId}/actions`);

  const handleDetailClick = () => {
    window.open(
      'https://uniquegoodcompany.notion.site/2d218b2eed60815d87dce6d2387a0970',
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleUpgradeClick = () => {
    popoverToggle();
    openPromotionModal({ triggerLocation: PromotionTriggerLocation.advanced_action_usage });
  };

  const createActionListItems = (
    types: string[],
    fallbackItems: ActionListProps['items']
  ): ActionListProps['items'] => {
    if (types && types.length > 0) {
      return types
        .map((type) => {
          const meta = ACTION_TYPE_METADATA[type];
          if (!meta) return null;
          return {
            id: type,
            content: meta.label,
            prefix: <Icon icon={meta.icon} size="20px" />,
            onAction: () => {
              popoverToggle();
              setActionType(type); // type casting if needed
            }
          };
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
    }
    return fallbackItems;
  };

  const generalListItems: ActionListProps['items'] = useMemo(
    () =>
      createActionListItems(
        actionTypesData?.basic || [],
        actionTypeGroups.General.map((item) => ({
          id: item.type,
          content: item.label,
          prefix: <Icon icon={item.icon} size="20px" />,
          onAction: () => {
            popoverToggle();
            setActionType(item.type);
          }
        }))
      ),
    [setActionType, actionTypesData]
  );

  const advancedListItems: ActionListProps['items'] = useMemo(
    () =>
      createActionListItems(
        actionTypesData?.advanced || [],
        actionTypeGroups.Advanced.map((item) => ({
          id: item.type,
          content: item.label,
          prefix: <Icon icon={item.icon} size="20px" />,
          onAction: () => {
            popoverToggle();
            setActionType(item.type);
          }
        }))
      ),
    [setActionType, actionTypesData]
  );

  // Free 플랜용 disabled 아이템 (클릭 불가, 회색 아이콘)
  const disabledAdvancedListItems: ActionListProps['items'] = useMemo(() => {
    const types = actionTypesData?.advanced;
    if (types && types.length > 0) {
      return types
        .map((type) => {
          const meta = ACTION_TYPE_METADATA[type];
          if (!meta) return null;
          return {
            id: type,
            content: meta.label,
            prefix: <Icon icon={meta.icon} size="20px" color={color.icon_disabled} />
          };
        })
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
    }

    return actionTypeGroups.Advanced.map((item) => ({
      id: item.type,
      content: item.label,
      prefix: <Icon icon={item.icon} size="20px" color={color.icon_disabled} />
    }));
  }, [actionTypesData]);

  const additionalListItems: ActionListProps['items'] = useMemo(
    () =>
      createActionListItems(
        actionTypesData?.addtional || [],
        actionTypeGroups.Additional.map((item) => ({
          id: item.type,
          content: item.label,
          prefix: <Icon icon={item.icon} size="20px" />,
          onAction: () => {
            popoverToggle();
            setActionType(item.type);
          }
        }))
      ),
    [setActionType, actionTypesData]
  );

  const renderListItems = useMemo(() => {
    if (plan === 'Free') {
      return (
        <>
          <Popover.Section>기본</Popover.Section>
          <PopoverAction>
            <ActionList items={generalListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
          </PopoverAction>
          <SectionHeader title="고급" showDetailLink onDetailClick={handleDetailClick} />
          <div css={LockedSectionWrapper}>
            <ActionList items={disabledAdvancedListItems} cssStyle={DisabledActionListStyle} />
            <div css={LockedScrimStyle}>
              <button type="button" css={UpgradeButtonStyle} onClick={handleUpgradeClick}>
                요금제 업그레이드
              </button>
            </div>
          </div>
        </>
      );
    }

    if (plan === 'Pro' || plan === 'Enterprise' || plan === 'Class' || plan === 'Teacher') {
      return (
        <>
          <Popover.Section>기본</Popover.Section>
          <PopoverAction>
            <ActionList items={generalListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
          </PopoverAction>
          <SectionHeader title="고급" showDetailLink onDetailClick={handleDetailClick} />
          <PopoverAction>
            <ActionList items={advancedListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
          </PopoverAction>
        </>
      );
    }

    return (
      <>
        <Popover.Section>기본</Popover.Section>
        <PopoverAction>
          <ActionList items={generalListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
        </PopoverAction>
        <SectionHeader title="고급" showDetailLink onDetailClick={handleDetailClick} />
        <PopoverAction>
          <ActionList items={advancedListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
        </PopoverAction>
        <Popover.Section>추가 기능</Popover.Section>
        <PopoverAction>
          <ActionList items={additionalListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
        </PopoverAction>
      </>
    );
  }, [
    generalListItems,
    advancedListItems,
    disabledAdvancedListItems,
    additionalListItems,
    handleDetailClick,
    handleUpgradeClick
  ]);

  return (
    <>
      <Popover
        contentCssStyle={PopoverScroll}
        active={popoverActive}
        onClose={popoverToggle}
        activator={
          <button type="button" css={QuestButtonWrap} onClick={popoverToggle}>
            {actionDefinition?.icon && <Icon icon={actionDefinition.icon} size="20px" />}
            <Typography as="p" type="button">
              {actionDefinition?.label}
            </Typography>

            {actionDefinition?.label !== '액션 없음' && (
              <Tooltip title={actionDefinition?.label} content={actionDefinition?.tooltip}>
                <Icon
                  cssStyle={MarginLeft4}
                  icon="question_circle_solid"
                  size="16px"
                  color={color.icon_disabled}
                />
              </Tooltip>
            )}

            <Typography as="p" type="button" textColor="subdued">
              변경
            </Typography>
          </button>
        }
      >
        {renderListItems}
        {/* <Popover.Section>기본</Popover.Section>
      <ActionList items={generalListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
      <Popover.Section>고급</Popover.Section>
      <ActionList items={advancedListItems} cssStyle={ActionTypeSelectorWidthOverrideStyle} />
      <Popover.Section>추가 기능</Popover.Section>
      <ActionList
        items={additionalListItems?.filter(
          (item) => !actionTypeDefinitions.find((def) => def.type === item.id)?.disabled || isAdmin
        )}
        cssStyle={ActionTypeSelectorWidthOverrideStyle}
      /> */}
      </Popover>
    </>
  );
};

export default ActionTypeSelector;

const QuestButtonWrap = css`
  display: flex;
  align-items: center;
  padding: 12px ${spacing.common.small};
  border: none;
  outline: none;
  margin: 0px;
  cursor: pointer;
  width: 100%;
  border-radius: ${spacing.common.small};
  background-color: transparent;

  &:hover {
    background-color: ${color.surface_default_hovered};
  }

  & > .IconWrap {
    margin: 0px;
    ${MarginRight8};
  }

  & > p:last-of-type {
    margin-left: auto;
    margin-right: 4px;
  }
`;
